/**
 * KpiCardGrid — responsive grid of five scorecard KPI cards.
 *
 * §5.BT §3.3 P3 Commit C (2026-06-19). Locked in
 *   docs/audits/2026-06-18_5bt_p3_3_dossier_p2_design.md §D.2
 *
 * Five cards (left-to-right, top-to-bottom):
 *   1. Overall Grade               (worst-of across scopes; A>B>C>D>F)
 *   2. On-Time Delivery %           (SCORECARD basis, spend-weighted, + a Wingman-measured
 *                                    divergence callout in the subtext -- see below)
 *   3. Spend (quarter)              (SUM)
 *   4. Cost of Poor Performance     (SUM late + vc)
 *   5. From the Quarterly Scorecard (src + Wingman)    (spend-weighted; partial_coverage caveat)
 *
 * Reads `quarters` from the endpoint response (DESC order from backend; index 0
 * is the most-recent quarter). Sparklines are built from the same `quarters`
 * array reversed to chronological order — left-to-right = oldest-to-newest,
 * matching the reading direction locked at P2 §D.4.
 *
 * Degenerate handling per P2 §D.4: when `quarters.length < 2`, sparklines hide
 * inside each KpiCard (KpiCard's own internal rule). When `quarters` is empty,
 * the grid still renders five "—" cards rather than disappearing.
 */
import React from 'react';
import KpiCard from './KpiCard';

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
  marginBottom: '24px',
};

const MOBILE_GRID_STYLE = {
  ...GRID_STYLE,
  gridTemplateColumns: 'repeat(2, 1fr)',
};

function buildSeries(quartersAsc, key) {
  return quartersAsc.map((q) => ({
    label: q.period_label || '',
    value: q.aggregated ? q.aggregated[key] : null,
  }));
}

/**
 * KpiCardGrid. Props:
 *   - quarters : array of quarter blocks from the endpoint response (DESC).
 *   - wingmanOnTimePct : the delivery-reliability engine's MEASURED on-time
 *     fraction (0..1), or null/undefined when unavailable (flag off, no promise
 *     dates yet, fetch failed).
 *
 *     Lane 402 (Ryan-LOCKED 2026-07-25, amending Lane 367's D-1): this is NOT the
 *     card's value. The On-Time-Delivery card's VALUE is the SCORECARD
 *     `agg.premium_ontime_pct`; wingmanOnTimePct feeds a DIVERGENCE CALLOUT in the
 *     subtext ("Q1 2026 · Wingman measures 51%"). Ryan, verbatim: "on the
 *     performance tab where we have on-time delivery -- this comes from their
 *     scorecards, I don't wanna change it ... I want the SCORECARD calculation to
 *     show on there, with a callout saying it's different than the Wingman
 *     calculation."
 *
 *     This card is the SOLE exception to the measured-only rule. Every OTHER
 *     on-time surface -- the dossier figure strip, the deck, chat, the projection --
 *     stays MEASURED-only ("And for everywhere we use the delivery stuff in the deck
 *     and all the other systems, that number needs to only be the Wingman
 *     calculation"). Do NOT read this card as licence to re-introduce a scorecard
 *     on-time anywhere else.
 *
 *     Full record incl. the escalation it resolves: docs/archive/consult-logs/2026-07-25_session.md
 *     Consult #1 + its RYAN'S RESOLUTION block.
 */
function KpiCardGrid({ quarters, wingmanOnTimePct }) {
  const list = quarters || [];
  // Backend returns DESC; first entry is most recent — that's the headline value.
  const latest = list[0] || null;
  const agg = latest && latest.aggregated ? latest.aggregated : {};
  // Sparkline series is chronological (oldest → newest, left → right).
  const chronological = [...list].reverse();
  const quarterSubtext = latest && latest.period_label ? latest.period_label : '';

  const partialCoverage = agg.wm_yoy_reconciliation_status === 'partial_coverage';

  return (
    <div style={GRID_STYLE} data-testid="kpi-card-grid">
      <KpiCard
        label="Overall Grade"
        grade={agg.overall_rating || null}
        subtext={quarterSubtext}
        series={buildSeries(chronological, 'overall_rating').map(p => ({
          ...p,
          // grade as series value isn't numeric — we hide that sparkline by
          // forcing all-null; KpiCard's <2-non-null rule then hides it.
          value: null,
        }))}
      />
      {/* Lane 402 (Ryan-LOCKED 2026-07-25) — amends Lane 367's D-1 for THIS CARD ONLY.
          The VALUE is the SCORECARD premium_ontime_pct (already a PERCENT from
          scorecard_aggregation.py:218 -- do NOT ×100; that factor belongs only to the
          measured 0..1 fraction), and the sparkline is the real scorecard quarterly
          series. The Wingman-MEASURED figure rides the existing `subtext` channel as a
          divergence callout, mirroring the "From the Quarterly Scorecard" card below --
          same grid, same mechanism, no new KpiCard prop.

          `quarterSubtext` STAYS in the callout and is load-bearing: the two figures have
          genuinely different windows (scorecard = one quarter; measured = trailing
          DELIVERY_WINDOW_MONTHS), so dropping the quarter label would leave the window
          named on NEITHER figure -- the window-vs-basis conflation Lane 359 had to
          Rule-112-correct. Ryan shortened only the Wingman clause ("just have it say
          Wingman measures 51%"), knowingly accepting that the Wingman figure carries no
          window; the scorecard figure keeps its quarter.

          Measured absent (flag off / no promise dates / fetch failed) -> the callout is
          omitted entirely and the subtext falls back to `quarterSubtext` alone. A missing
          measured figure is never narrated and never fabricated. */}
      <KpiCard
        label="On-Time Delivery"
        value={agg.premium_ontime_pct}
        format="pct"
        subtext={
          wingmanOnTimePct === null || wingmanOnTimePct === undefined
            ? quarterSubtext
            : [quarterSubtext, `Wingman measures ${Math.round(Number(wingmanOnTimePct) * 100)}%`]
              .filter(Boolean).join(' · ')
        }
        series={buildSeries(chronological, 'premium_ontime_pct')}
      />
      <KpiCard
        label="Spend (Quarter)"
        value={agg.total_value_received}
        format="currency"
        subtext={quarterSubtext}
        series={buildSeries(chronological, 'total_value_received')}
      />
      <KpiCard
        label="Cost of Poor Performance"
        value={agg.cost_of_poor_performance}
        format="currency"
        subtext={quarterSubtext}
        series={buildSeries(chronological, 'cost_of_poor_performance')}
      />
      {/* Lane 134 rename (spec §2.2, Ryan 2026-07-15): the label states
          provenance (the figure came from the scorecard file unaltered), not a
          claim about who authored it — the old "Supplier-Reported" overclaimed. */}
      <KpiCard
        label="From the Quarterly Scorecard"
        value={agg.src_price_change_pct}
        format="pct_signed"
        subtext={
          agg.wm_yoy_price_change_pct === null || agg.wm_yoy_price_change_pct === undefined
            ? quarterSubtext
            : `${quarterSubtext} · Wingman ${Number(agg.wm_yoy_price_change_pct) >= 0 ? '+' : ''}${Number(agg.wm_yoy_price_change_pct).toFixed(1)}%`
        }
        series={buildSeries(chronological, 'src_price_change_pct')}
        partialCoverage={partialCoverage}
      />
    </div>
  );
}

export { MOBILE_GRID_STYLE };
export default KpiCardGrid;
